import { debounce } from '@mui/material';
import type {
  DataGridPremiumProps,
  GridColumnVisibilityModel,
  GridInitialState as GridInitialStatePremium,
} from '@mui/x-data-grid-premium';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import mapObject from 'src/utils/assignObject';
import { stripEmptyFilters } from '../shared/filterSanitize';
import {
  dropNonFiniteFilterConditions,
  dropNonFiniteFilterItems,
  dropPersistedNonFiniteFilterItems,
} from '../shared/finiteFilterValues';
import type { ServerGridFilter } from '../shared/gridFilterTypes';

/**
 * Persisted column state — order · visibility · width · pinning, keyed by `storageKey`.
 *
 *   localStorage `${storageKey}-column-state`
 *        ▲ write   debounced 500ms · whole apiRef.exportState()
 *        │         + serverFilters · quickFilter · migrationKey
 *        ▼ read    once on mount · discarded unless migrationKey matches
 *   initialState ──▶ DataGridPremium
 *
 * The KEY FORMAT is load-bearing twice over: every user's saved layout already
 * lives under this name, and `useGridRows` reads `serverFilters` back out of
 * the same blob.
 */

/** Do NOT load persisted state unless the key matches: an incompatible column-definition change (e.g. dropped filter operations) blows up the page. Change the key when you change the definitions. */
export const DATAGRID_STATE_MIGRATION_KEY = 4;

/** What the grid actually writes under `${storageKey}-column-state` — the MUI export plus three extras. */
type PersistedGridState = GridInitialStatePremium & {
  migrationKey?: number;
  /** `hiddenByDefault` fields already applied once to this saved layout; the user's own choice wins after that. */
  hiddenByDefaultApplied?: string[];
  serverFilters?: ServerGridFilter[];
  quickFilter?: string;
};

export type GridColumnStateOptions = {
  storageKey: string;
  columns: Readonly<Record<string, unknown>>;
  apiRef: NonNullable<DataGridPremiumProps['apiRef']>;
  initialState?: GridInitialStatePremium;
  /** Caller-controlled visibility, applied over the user's own model. */
  columnVisibilityModel?: GridColumnVisibilityModel;
  /** App-forced visibility. An override, never state — see the invariant below. */
  disabledColumns?: Record<string, boolean>;
  /**
   * Columns the grid starts hidden; the user can still show them from the gear. A saved layout gets them hidden
   * ONCE (layouts saved before this list existed hold an explicit `true` for every column); afterwards the user's
   * choice is kept, and a field the saved layout never recorded starts hidden.
   */
  hiddenByDefault?: readonly string[];
  toolbar?: unknown;
  toolbarSlot?: unknown;
  /** Persisted alongside the grid state so `useGridRows` can rehydrate the filter chips. */
  serverFilters?: ServerGridFilter[];
  /** Persisted alongside the grid state so `useGridRows` can rehydrate the quick filter. */
  quickFilterLabel?: string;
};

export type GridColumnState = {
  /** Debounced persist of the whole exported grid state. Wire it to every MUI change callback that should survive a reload. */
  persistGridState: () => void;
  /** `initialState` for DataGridPremium — the persisted state merged over the caller's. */
  initialState: GridInitialStatePremium;
  /** `columnVisibilityModel` for DataGridPremium — user model under caller override, forced columns and the LD kill-switch. */
  columnVisibilityModel: GridColumnVisibilityModel;
  /** `onColumnVisibilityModelChange` for DataGridPremium — strips forced entries before persisting or storing. */
  onColumnVisibilityModelChange: (model: GridColumnVisibilityModel) => void;
  /** Fields the app forces hidden (`true`) or visible (`false`) on every render: `disabledColumns` plus the row-actions column. Never user-toggleable. */
  forcedColumns: Record<string, boolean>;
};

const NOTHING_HIDDEN_BY_DEFAULT: readonly string[] = [];

const readPersistedState = (storageKey: string): PersistedGridState | null => {
  try {
    return JSON.parse(localStorage.getItem(`${storageKey}-column-state`) ?? 'null');
  } catch {
    return null;
  }
};

export function useGridColumnState({
  storageKey,
  columns,
  apiRef,
  initialState,
  columnVisibilityModel,
  disabledColumns,
  hiddenByDefault = NOTHING_HIDDEN_BY_DEFAULT,
  toolbar,
  toolbarSlot,
  serverFilters,
  quickFilterLabel,
}: GridColumnStateOptions): GridColumnState {
  /**
   * The row-actions column (the ⋯ menu) can't be hidden by the user: the chooser never lists
   * it, so a stored `actions: false` (the TAS-3791 F1 leak) could never be undone. It is
   * forced visible like a `disabledColumns: { actions: false }` entry — overridden on render,
   * stripped on the next persist. Only the app hides it: `disabledColumns.actions === true`
   * or a caller-controlled `columnVisibilityModel.actions`.
   */
  const hasActionsColumn = 'actions' in columns;
  const callerActionsVisibility = columnVisibilityModel?.actions;
  const forcedColumns = useMemo(
    (): Record<string, boolean> =>
      hasActionsColumn && callerActionsVisibility === undefined
        ? { actions: false, ...disabledColumns }
        : (disabledColumns ?? {}),
    [hasActionsColumn, callerActionsVisibility, disabledColumns]
  );

  /**
   * INVARIANT: `disabledColumns` is an app-forced override re-applied over the user's model
   * every render — it must NEVER enter React state or localStorage, or a persisted copy
   * outlives the force that produced it.
   */
  const stripForcedVisibility = useCallback(
    (model: Record<string, boolean>): Record<string, boolean> =>
      Object.fromEntries(Object.entries(model ?? {}).filter(([field]) => !(field in forcedColumns))),
    [forcedColumns]
  );

  /**
   * Deliberately NOT memoised: a fresh debounce per render is the current
   * timing, and the effect that persists filter changes keys off its identity.
   */
  const persist = debounce((visibility?: GridColumnVisibilityModel) => {
    if (!apiRef.current) return;

    const state: PersistedGridState = apiRef.current.exportState();

    if (visibility) {
      if (!state.columns) {
        state.columns = { columnVisibilityModel: visibility };
      } else {
        state.columns.columnVisibilityModel = visibility;
      }
    }

    const stored = readPersistedState(storageKey);
    const storedApplied =
      stored?.migrationKey === DATAGRID_STATE_MIGRATION_KEY ? (stored.hiddenByDefaultApplied ?? []) : [];
    const hiddenByDefaultApplied = [...new Set([...storedApplied, ...hiddenByDefault])];

    if (state.columns?.columnVisibilityModel) {
      // A field another grid on this key keeps hidden-by-default, but this grid forces, keeps its stored choice.
      const storedModel = stored?.columns?.columnVisibilityModel ?? {};
      const carried = Object.fromEntries(
        hiddenByDefaultApplied
          .filter((field) => field in forcedColumns && storedModel[field] !== undefined)
          .map((field) => [field, storedModel[field]])
      );
      state.columns.columnVisibilityModel = {
        ...stripForcedVisibility(state.columns.columnVisibilityModel),
        ...carried,
      };
    }

    if (state.filter?.filterModel) {
      state.filter = { ...state.filter, filterModel: dropNonFiniteFilterItems(state.filter.filterModel) };
    }

    const fullState = {
      ...state,
      serverFilters: serverFilters && stripEmptyFilters(dropNonFiniteFilterConditions(serverFilters)),
      quickFilter: quickFilterLabel,
      migrationKey: DATAGRID_STATE_MIGRATION_KEY,
      ...(hiddenByDefaultApplied.length > 0 && { hiddenByDefaultApplied }),
    };

    localStorage.setItem(`${storageKey}-column-state`, JSON.stringify(fullState));
  }, 500);

  const persistGridState = () => persist();

  const loadedInitialState = useMemo((): GridInitialStatePremium => {
    const stateJSON = localStorage.getItem(`${storageKey}-column-state`);

    const genInitialVisModel = (): GridColumnVisibilityModel => {
      const initialVis = initialState?.columns?.columnVisibilityModel;

      return mapObject(columns, (key) => {
        // `disabledColumns` is deliberately NOT consulted here — it is applied
        // at the prop level (`forcedVisibility`), never baked into this model.
        if (columnVisibilityModel?.[key] !== undefined) {
          return { [key]: columnVisibilityModel?.[key] };
        }

        // use existing column vis model if it exists
        return { [key]: initialVis?.[key] ?? !hiddenByDefault.includes(key) };
      });
    };

    if (stateJSON) {
      const state: PersistedGridState = JSON.parse(stateJSON);

      // always load aggregation and page directly from grid
      delete state.aggregation;
      delete state.pagination?.paginationModel?.page;

      //...i'm not sure if this is good, since saved filters are also pulled a similar method. I think instead, we could be more diligent with what we store in the state.
      if (state.migrationKey === DATAGRID_STATE_MIGRATION_KEY) {
        if (state.filter?.filterModel) {
          state.filter = { ...state.filter, filterModel: dropPersistedNonFiniteFilterItems(state.filter.filterModel) };
        }

        // DataGrid only saves the visibility of columns that are in the columnVisibilityModel,
        // so it neeeds to be initialized with all columns
        // when the user does not have the option to hide columns, the columnVisibilityModel should not be restored
        if (!toolbar && !toolbarSlot) {
          state.columns = { ...(state.columns || {}), columnVisibilityModel: genInitialVisModel() };
        } else if (hiddenByDefault.length > 0) {
          const savedModel = state.columns?.columnVisibilityModel ?? {};
          const applied = state.hiddenByDefaultApplied ?? [];
          const hiddenNow = hiddenByDefault.filter(
            (field) => !applied.includes(field) || savedModel[field] === undefined
          );
          state.columns = {
            ...(state.columns || {}),
            columnVisibilityModel: { ...savedModel, ...Object.fromEntries(hiddenNow.map((field) => [field, false])) },
          };
        }

        return state;
      }
    }

    return { columns: { ...(initialState?.columns || {}), columnVisibilityModel: genInitialVisModel() } };
  }, [storageKey, initialState?.columns, columns, columnVisibilityModel, toolbar, toolbarSlot, hiddenByDefault]);

  const forcedVisibility = useMemo(
    (): GridColumnVisibilityModel => mapObject(forcedColumns, (key) => ({ [key]: !forcedColumns[key] })),
    [forcedColumns]
  );

  const [visibilityModel, setColumnVisibility] = useState(
    loadedInitialState.columns?.columnVisibilityModel ?? initialState?.columns?.columnVisibilityModel
  );

  const flags = useFlags();
  const overrideVisibility: GridColumnVisibilityModel = flags['disabledColumns']?.[storageKey] ?? {};

  const mergedState = useMemo(
    (): GridInitialStatePremium => ({ ...initialState, ...loadedInitialState }),
    [initialState, loadedInitialState]
  );

  const nextVisibilityModel = {
    ...visibilityModel,
    ...columnVisibilityModel,
    ...forcedVisibility,
    ...overrideVisibility,
  };

  /** MUI applies a controlled `columnVisibilityModel` in an effect AFTER the render that passed it, so its column virtualizer sizes that render to the OLD visible columns; one more render after the change commits lets it re-measure. */
  const visibilityKey = JSON.stringify(nextVisibilityModel);

  /** The `columnVisibilityModel` must keep its identity until its CONTENT changes: MUI's effect keys off identity alone, so a fresh object rebuilds every column definition and re-renders every row and cell on every host render. */
  // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by content on purpose; see above
  const mergedVisibilityModel = useMemo(() => nextVisibilityModel, [visibilityKey]);
  const committedVisibilityKey = useRef(visibilityKey);
  const [, rerenderGrid] = useReducer((renders: number) => renders + 1, 0);
  useEffect(() => {
    if (committedVisibilityKey.current === visibilityKey) return;
    committedVisibilityKey.current = visibilityKey;
    rerenderGrid();
  }, [visibilityKey]);

  return {
    persistGridState,
    initialState: mergedState,
    columnVisibilityModel: mergedVisibilityModel,
    onColumnVisibilityModelChange: (update) => {
      const userModel = stripForcedVisibility(update);

      persist(userModel);
      setColumnVisibility(userModel);
    },
    forcedColumns,
  };
}
